import React, { useReducer } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { examGet, examGetForVisit, inductionSeen, inductionSeenForVisit, questionaryPost, questionaryPostForVisit, sendInductionCodeForVisitService, sendInductionCodeService, resetExam } from '../../services/examService'
import ExamContext from './ExamContext'
import ExamReducer from './ExamReducer'

export const ExamState = ({ children }) => {

    const initialState = {
        exam: null,
        isFetching: false,
        changingPage: false,
        sending: false,
        error: false,
        message: '',
        alert: false,
        verify: false,
    }

    const { t } = useTranslation();

    const [state, dispatch] = useReducer(ExamReducer, initialState);

    const navigate = useNavigate();

    const setExam = (exam) => {
        dispatch({
            type: 'SET_EXAM',
            payload: exam
        });
    };

    const setIsFetching = (isFetching) => {
        dispatch({
            type: 'SET_IS_FETCHING',
            payload: isFetching
        });
    };

    const setChangingPage = (changingPage) => {
        dispatch({
            type: 'SET_CHANGING_PAGE',
            payload: changingPage
        });
    };

    const setSending = (sending) => {
        dispatch({
            type: 'SET_SENDING',
            payload: sending
        });
    };

    const setError = (error) => {
        dispatch({
            type: 'SET_ERROR',
            payload: error
        });
    };

    const setMessage = (message) => {
        dispatch({
            type: 'SET_MESSAGE',
            payload: message
        });
    };

    const setAlert = (alert) => {
        dispatch({
            type: 'SET_ALERT',
            payload: alert
        });
    };

    const setVerify = (verify) => {
        dispatch({
            type: 'SET_VERIFY',
            payload: verify
        });
    };

    const getExam = async (inductionId, employeeId, verificationCode) => {
        try {
            setIsFetching(true);

            const response = await examGet(inductionId, employeeId, verificationCode);

            if (response.ok) {
                setExam(response.data);
                setError(false);
                setMessage('');
                setVerify(true)
            } else {
                setError(true);

                const errorMessages = {
                    404: t('questionary.not_found'),
                    401: t('verification.no_valid_code')
                }

                setMessage(errorMessages[response.data.status] || t('common.error_server'));
            }

        } catch (e) {
            console.log(e)
            setError(true);
            setMessage(t('common.error_server'));
        } finally {
            setIsFetching(false);
        }
    }

    const postQuestionary = async (questionaryValues, visualizationId) => {
        try {
            setSending(true);
            const response = await questionaryPost(questionaryValues, visualizationId);
            if (response.ok) {
                setError(false);
                setMessage('');
                setExam(response.data);
            } else {
                setError(true);
                setMessage(t('common.error_server'));
            }
        } catch (e) {
            console.log(e)
            setError(true);
            setMessage(t('common.error_server'));
        } finally {
            setSending(false);
        }
    }

    const seenInduction = async ({ inductionValues, visualizationId, questionaryValues, verificationCode }) => {
        try {
            setSending(true);
            setError(false);
            const response = await inductionSeen(inductionValues, visualizationId);
            if (response.ok) {
                if (questionaryValues !== undefined) {
                    await postQuestionary(questionaryValues, visualizationId)
                } else {
                    await getExam(state.exam.induction._id, inductionValues.employee, verificationCode)
                }
                navigate(`/exam/result/${state.exam.induction._id}/employee/${inductionValues.employee}`);
            } else {
                setError(true);
                setMessage(t('common.error_server'));
            }
        } catch (e) {
            console.log(e)
            setError(true);
            setMessage(t('common.error_server'));
        } finally {
            setSending(false);
        }
    }

    const getExamForVisit = async (inductionId, visitId, verificationCode) => {
        try {
            setIsFetching(true);
            const response = await examGetForVisit(inductionId, visitId, verificationCode);
            if (response.ok) {
                setExam(response.data);
                setError(false);
                setVerify(true)
            } else {
                setError(true);

                const errorMessages = {
                    404: t('questionary.not_found'),
                    401: t('verification.no_valid_code')
                }

                setMessage(errorMessages[response.data.status] || t('common.error_server'));

            }
        } catch (e) {
            console.log(e)
            setError(true);
            setMessage(t('common.error_server'));
        } finally {
            setIsFetching(false);
        }
    }

    const postQuestionaryForVisit = async (questionaryValues) => {
        try {
            setSending(true);
            const response = await questionaryPostForVisit(questionaryValues);
            if (response.ok) {
                setError(false);
                setMessage('');
                setExam(response.data);
            } else {
                setError(true);
                setMessage(t('common.error_server'));
            }
        } catch (e) {
            console.log(e)
            setError(true);
            setMessage(t('common.error_server'));
        } finally {
            setSending(false);
        }
    }

    const seenInductionForVisit = async ({ inductionValues, questionaryValues, verificationCode }) => {
        try {
            setSending(true);
            setError(false);
            setMessage('');
            const response = await inductionSeenForVisit(inductionValues);
            if (response.ok) {
                if (questionaryValues !== undefined) {
                    await postQuestionaryForVisit(questionaryValues)
                } else {
                    await getExamForVisit(inductionValues.induction, inductionValues.visit, verificationCode)
                }
                navigate(`/exam/result/${inductionValues.induction}/visit/${inductionValues.visit}`);
            } else {
                setError(true);
                setMessage(t('common.error_server'));
            }
        } catch (e) {
            console.log(e)
            setError(true);
            setMessage(t('common.error_server'));
        } finally {
            setSending(false);
        }
    }

    const sendVerifyCodeToMail = async (employeeId, inductionId) => {
        try {

            setIsFetching(true);

            setError(false);

            setMessage('');

            const response = await sendInductionCodeService(employeeId, inductionId);

            if (response.ok) {
                setError(false);
            } else {
                setError(true);

                const errorMessages = {
                    401: t('verification.no_valid_code'),
                    410: t('verification.link_expired')
                }

                setMessage(errorMessages[response.data.status] || t('common.error_server'));
            }

        } catch (e) {
            console.log(e);
            setError(true);
            setMessage(t('common.error_server'));
        } finally {
            setIsFetching(false);
        }
    }

    const sendVerifyCodeToVisitMail = async (visitId, inductionId) => {
        try {

            setIsFetching(true);

            setError(false);

            setMessage('');

            const response = await sendInductionCodeForVisitService(inductionId, visitId);

            if (response.ok) {
                setError(false);
            } else {
                setError(true);

                const errorMessages = {
                    401: t('verification.no_valid_code'),
                    410: t('verification.link_expired')
                }

                setMessage(errorMessages[response.data.status] || t('common.error_server'));
            }

        } catch (e) {
            console.log(e);
            setError(true);
            setMessage(t('common.error_server'));
        } finally {
            setIsFetching(false);
        }
    }

    const resetEmployeeExam = async ({ visualizationId, employeeId, contractorId }) => {
        try {
            setSending(true);
            setError(false);
            setMessage('');
            const response = await resetExam(visualizationId, employeeId, contractorId);
            if (response?.ok) {
                setError(false);
                setMessage('');
                return true;
            } else {
                setError(true);
                setMessage(t('common.error_server'));
                return false;
            }
        } catch (e) {
            console.log(e);
            setError(true);
            setMessage(t('common.error_server'));
            return false;
        } finally {
            setSending(false);
        }
    }

    return (
        <ExamContext.Provider value={{
            examState: state,
            getExam,
            seenInduction,
            postQuestionary,
            getExamForVisit,
            seenInductionForVisit,
            sendVerifyCodeToMail,
            setExam,
            setIsFetching,
            setChangingPage,
            setSending,
            setError,
            setMessage,
            setAlert,
            setVerify,
            sendVerifyCodeToVisitMail,
            resetEmployeeExam,
        }}>
            {children}
        </ExamContext.Provider>
    )
}
